import { useEffect, useRef } from 'react';

function useOnClickOutside(handler: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest handler in a ref so the document-level listeners always
  // call the current value. The previous implementation memoised the handler
  // with an empty dep array, capturing the first-render reference forever —
  // so consumers that swap their `onClose` over time (e.g. a drawer that
  // gates close-while-submitting via `isSubmitting ? noop : onClose`) were
  // silently bypassed.
  const handlerRef = useRef(handler);
  // EWM-760 — react-hooks/refs: writing ref.current during render is flagged.
  // Move the assignment into an effect (no deps → runs after every render,
  // before any subsequent document-level event can fire), so `handlerRef` is
  // always up to date by the time the async listeners below read it.
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    // EWM-728 — record where the press began. A drag that STARTS inside the
    // guarded element (e.g. selecting text in a password field and releasing
    // the mouse past the element's edge) must not count as an outside click,
    // even when the mouseup lands outside. Inspecting only the mouseup target
    // treated that text-selection drag as a dismissal, closing the drawer and
    // wiping the form. Only dismiss when BOTH the press and the release are
    // outside.
    let pressStartedInside = false;

    const handlePressStart = (event: MouseEvent | TouchEvent) => {
      pressStartedInside = !!(ref.current && ref.current.contains(event.target as Node));
    };

    const handleMouseUp = (event: MouseEvent | TouchEvent) => {
      const startedInside = pressStartedInside;
      pressStartedInside = false;
      if (startedInside) return;
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handlerRef.current();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Only react to Escape while the guarded element is actually mounted.
      // Consumers that conditionally unmount their ref'd element (drawers,
      // slide-out panels) keep these document listeners attached even when
      // closed; without this guard, Escape anywhere on the page fires the
      // handler with no panel open. The mouseUp path is already implicitly
      // guarded by the `ref.current.contains` check in `handleMouseUp` above.
      if (event.key === 'Escape' && ref.current) {
        handlerRef.current();
      }
    };

    document.addEventListener('mousedown', handlePressStart);
    document.addEventListener('touchstart', handlePressStart);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('mousedown', handlePressStart);
      document.removeEventListener('touchstart', handlePressStart);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return { ref };
}

export default useOnClickOutside;
